import { Box, Flex } from '@igooods/ui-kit';
import React, { useState } from 'react';
import SimplePopup from '../Popups/SimplePopup';
import { declOfNum } from '../../utils/helpers';
import { EnumSize } from '../../constants/enums';
import OrderActions from '../../Modules/Cart/actions';
import { useDispatch } from 'react-redux';
import { IPointsPopupProps } from './Types/types';
import ThemeButton from 'Components/ThemedButton';
import styled from 'styled-components';
import { mediaSmDown } from '../../theme/helpers/css';
import { Text, Title } from '../Typography';
import { colors } from '../../constants/colors';
import Input from '../Input';
import { FontFamily } from '../../theme';

const POPUP_WIDTH_DESKTOP = 456;

export const Container = styled(Flex).attrs({
  center: true,
  middle: true,
  column: true,
  pb: 14,
})`
  width: ${POPUP_WIDTH_DESKTOP}px;
  padding-bottom: 56px;
  ${mediaSmDown`
    width: 100%;
    padding-bottom: 0;
  `}
`;

const TitleContainer = styled(Box).attrs({ mt: 15, mtXs: 5 })`
  ${mediaSmDown`
    width: 100%;
  `}
`;

export const TitleStyled = styled(Title).attrs({ center: true })`
  font-size: 24px;
  line-height: 30px;
  color: ${colors.blackRussian};
  ${mediaSmDown`
    width: 100%;
    font-size: 20px;
    line-height: 26px;
    text-align: start;
  `}
`;

export const TextContainer = styled(Box).attrs({ mt: 2.5, mtXs: 3, pxXs: 3.25 })`
  width: 100%;
  padding: 0 57px;

  ${mediaSmDown`
    width: 100%;
    padding: 0;
  `}
`;

export const PointsInput = styled(Input)<{
  errorMessage?: string;
}>`
  width: 336px;
  height: 48px;
  border-radius: 100px;
  background-color: ${colors.ghostWhite} !important;
  font-size: 13px;
  font-weight: 400;
  color: ${colors.blackRussian};

  &:hover,
  &:focus {
    background-color: ${colors.ghostWhite} !important;
  }

  & span {
    display: none !important;
    visibility: hidden;
  }

  ${({ errorMsg }) => Boolean(errorMsg) && `border: 1px solid ${colors.brinkPink}`}

  ${mediaSmDown`
    width: 100%;
  `}
`;

export const TextStyled = styled(Text).attrs({
  center: true,
})`
  font-size: 13px;
  line-height: 16px;
  letter-spacing: -0.2px;
  width: 100%;
  color: ${colors.paleBlackRussian};

  ${mediaSmDown`
    font-size: 14px;
    line-height: 22px;
    letter-spacing: -0.5px;
    text-align: start;
  `}
`;

export const PointsInfo = styled(Flex).attrs({ mt: 6, mtXs: 5.5, mb: 4 })`
  color: ${colors.blackRussian};
  font-size: 16px;
  align-items: center;
  justify-content: center;

  ${mediaSmDown`
    width: 100%;
    justify-content: flex-start;
    font-size: 14px;
  `}
`;

export const SubmitBtn = styled(ThemeButton)`
  margin-top: 52px;
  box-sizing: content-box;
  font-family: ${FontFamily.secondFont};
  width: 220px;
  height: 48px;
  border-radius: 100px;
  align-items: center;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;

  &:disabled {
    background-color: ${colors.buttonLightGreen};
    color: ${colors.white};
  }

  ${mediaSmDown`
     margin-top: 36px;
     width: 100%;
     justify-content: center;
     padding: 0;
  `}
`;

export const InputContainer = styled.div`
  width: 100%;
  padding: 0 55px 0 65px;

  span {
    display: none;
  }

  ${mediaSmDown`
      padding: 0;
  `}

  div {
    ${mediaSmDown`
      width: 100% !important;
    `}
  }
`;

const PointsPopup: React.FC<IPointsPopupProps> = ({ isShow, onClose, points, setTotalPointsToSpend, maxPoints }) => {
  const [pointsAmount, setPointsAmount] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const dispatch = useDispatch();

  const handleClose = () => {
    onClose();
    setErrorMessage('');
    setPointsAmount('');
  };

  const handleChangeInput = (value: string) => {
    setPointsAmount(value);
    setErrorMessage('');
  };

  const setPoints = async () => {
    const pointsInNumber = Number(pointsAmount);
    const isNumber = /^[0-9]+$/.test(pointsAmount.trim());
    if (!isNumber) {
      setErrorMessage('Введите целое числовое значение');

      return;
    }

    if (points < pointsInNumber) {
      setErrorMessage('Недостаточно баллов на счету');

      return;
    }

    const resultPoints = pointsInNumber > maxPoints ? maxPoints : pointsInNumber;
    try {
      await dispatch(OrderActions.spendPoints(resultPoints));
      setTotalPointsToSpend(resultPoints);
      handleClose();
    } catch (error) {
      if (!!error.response.data.error?.length) {
        setErrorMessage(error.response.data.error);
      }
      if (error.response.data.code === 10 && !error.response.data.error?.length) {
        setErrorMessage('Введите целое числовое значение');
      }
    }
  };

  return (
    <SimplePopup show={isShow} close={handleClose}>
      <Container>
        <TitleContainer>
          <TitleStyled>
            Cколько баллов <br /> спишем?
          </TitleStyled>
        </TitleContainer>
        <TextContainer>
          <TextStyled>
            У вас {points} {declOfNum(points, ['балл', 'балла', 'баллов'])}
            <br /> Можно списать не больше суммы заказа
          </TextStyled>
        </TextContainer>
        <PointsInfo>1 балл = 1 ₽</PointsInfo>
        <InputContainer>
          <PointsInput
            sizeName={EnumSize.lg}
            onChangeInput={handleChangeInput}
            defaultValue={pointsAmount}
            errorMsg={errorMessage}
          />
        </InputContainer>
        <SubmitBtn disabled={Boolean(errorMessage) || !pointsAmount.trim() || pointsAmount === '0'} onClick={setPoints}>
          Списать
        </SubmitBtn>
      </Container>
    </SimplePopup>
  );
};

export default PointsPopup;
